/**
 * sceneTransitionState — pure helpers for the painted-scene transition
 * controller (AGE-1421).
 *
 * Phaser-free by design so the state machine, easing, and audio-crossfade
 * timing math can be unit-tested without a renderer. The Phaser-aware bits
 * live in `SceneTransition.ts` (router) and `PaintedSceneHost.ts` (renderer).
 *
 * Three transition kinds, each with its own default duration and visual
 * choreography (see `docs/design/scene-transitions-painted.md` §4):
 *
 *   - `edge-crossfade`   ~600 ms   walker walks off a frame edge; B fades
 *                                  in over A. Walker is repositioned at B's
 *                                  entry point before the fade so the plate
 *                                  crossfade hides the snap.
 *   - `doorway`          ~700 ms   walker steps through a door. Camera
 *                                  dollies toward the doorway anchor; a
 *                                  vignette fades through black, scene
 *                                  swaps under it, vignette fades out.
 *   - `camera-pan`       ~1200 ms  for scenes that share a horizon — A and
 *                                  B render side-by-side; camera glides
 *                                  across the seam.
 *
 * Coordinate convention: every spatial value coming in/out of these helpers
 * is normalised in [0,1] (see `journeyManifest.schema.ts`). Conversion to
 * pixels is the renderer's job.
 */

import type { TransitionKind, NormalisedPoint } from './journeyManifest.schema.js';

// ── public types ───────────────────────────────────────────────────────────

/** Phases of a single transition run. Linear, no skip-ahead. */
export type TransitionPhase =
  | 'idle'
  | 'preparing'
  | 'crossfading'
  | 'settling'
  | 'done';

export interface TransitionState {
  phase: TransitionPhase;
  kind: TransitionKind;
  /** Source scene key. */
  fromKey: string;
  /** Target scene key. */
  toKey: string;
  /** Total duration including settle, in ms. */
  durationMs: number;
  /** `performance.now()` (or scene-time-now) when the transition started. */
  startedAt: number;
}

export const IDLE_TRANSITION_STATE: TransitionState = {
  phase: 'idle',
  kind: 'edge-crossfade',
  fromKey: '',
  toKey: '',
  durationMs: 0,
  startedAt: 0,
};

/**
 * Default transition durations in ms. These match the per-kind targets in
 * `scene-transitions-painted.md` and are the values the router uses unless
 * the manifest's `EdgeZone.durationMs` overrides for a specific zone.
 */
export const DEFAULT_DURATIONS_MS: Record<TransitionKind, number> = {
  'edge-crossfade': 600,
  doorway: 700,
  'threshold-zoom': 800,
  'camera-pan': 1200,
  'edge-pan': 0,
  wash: 900,
};

/** Hard floor — anything shorter than this reads as a cut, not a transition. */
export const MIN_TRANSITION_MS = 200;
/** Hard ceiling — beyond this we'd be holding a still frame; treat as a wash. */
export const MAX_TRANSITION_MS = 4000;

/** Per-phase budget for each kind. Values sum to 1.0. */
interface PhaseBudget {
  preparing: number;
  crossfading: number;
  settling: number;
}

/**
 * The visual choreography for each transition kind divides its total
 * duration across the three active phases. `preparing` is the camera-zoom
 * or capture-frame setup, `crossfading` is the actual fade/pan, `settling`
 * is the camera-reset / walker-step-forward tail.
 */
export const PHASE_BUDGETS: Record<TransitionKind, PhaseBudget> = {
  // Crossfade is mostly the fade itself — no camera prep, tiny settle.
  'edge-crossfade': { preparing: 0.05, crossfading: 0.85, settling: 0.10 },
  // Doorway: dolly-in (40%), vignette swap (40%), step-out (20%).
  doorway: { preparing: 0.4, crossfading: 0.4, settling: 0.2 },
  // Threshold-zoom: dolly into door (40%), reveal through door mask (40%), settle (20%).
  'threshold-zoom': { preparing: 0.4, crossfading: 0.4, settling: 0.2 },
  // Camera-pan: brief snap (5%), the actual pan (85%), settle (10%).
  'camera-pan': { preparing: 0.05, crossfading: 0.85, settling: 0.1 },
  // Edge-pan: walker-driven, no timed phases.
  'edge-pan': { preparing: 0, crossfading: 1, settling: 0 },
  // Wash: one continuous noise sweep, almost no setup or settle.
  wash: { preparing: 0.05, crossfading: 0.9, settling: 0.05 },
};

/**
 * Begin a transition. Returns a fresh state regardless of what was
 * previously in flight — a stale interrupted transition gets reset.
 */
export function beginTransition(
  kind: TransitionKind,
  fromKey: string,
  toKey: string,
  durationMs: number,
  now: number,
): TransitionState {
  return {
    phase: 'preparing',
    kind,
    fromKey,
    toKey,
    durationMs: clampDuration(durationMs),
    startedAt: now,
  };
}

/** Clamp a duration to the min/max allowed range. */
export function clampDuration(ms: number): number {
  if (!Number.isFinite(ms) || ms <= 0) return MIN_TRANSITION_MS;
  if (ms < MIN_TRANSITION_MS) return MIN_TRANSITION_MS;
  if (ms > MAX_TRANSITION_MS) return MAX_TRANSITION_MS;
  return ms;
}

/**
 * Resolve the duration to use for a transition. Manifest overrides win;
 * otherwise the default for the kind applies.
 */
export function resolveDuration(
  kind: TransitionKind,
  override: number | undefined,
): number {
  return clampDuration(override ?? DEFAULT_DURATIONS_MS[kind]);
}

/** Phase ordering — used to validate `advancePhase` transitions. */
const PHASE_ORDER: TransitionPhase[] = ['idle', 'preparing', 'crossfading', 'settling', 'done'];

/**
 * Advance the state machine. Only forward moves are allowed; a backwards
 * jump returns the same state unchanged so a buggy caller can't unwind a
 * transition that's already past it. `done` bounces back to `idle`.
 */
export function advancePhase(
  state: TransitionState,
  next: TransitionPhase,
): TransitionState {
  if (next === 'idle') return IDLE_TRANSITION_STATE;
  const fromIdx = PHASE_ORDER.indexOf(state.phase);
  const toIdx = PHASE_ORDER.indexOf(next);
  if (toIdx <= fromIdx) return state;
  return { ...state, phase: next };
}

/** True while a transition is mid-flight — blocks input + new transitions. */
export function isTransitionActive(state: TransitionState): boolean {
  return state.phase !== 'idle' && state.phase !== 'done';
}

// ── easing ─────────────────────────────────────────────────────────────────

/** Clamp a number to [0, 1]. */
export function clamp01(t: number): number {
  if (t <= 0) return 0;
  if (t >= 1) return 1;
  return t;
}

/**
 * Ease-in-out cubic. Used for plate crossfade alpha curves and camera-pan
 * scroll position so the motion accelerates and decelerates softly.
 */
export function easeInOutCubic(t: number): number {
  const x = clamp01(t);
  return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
}

/**
 * Smoothstep — softer than linear, cheaper than cubic. Good for the
 * doorway vignette mask alpha.
 */
export function smoothstep(t: number): number {
  const x = clamp01(t);
  return x * x * (3 - 2 * x);
}

// ── progress / phase derivation ────────────────────────────────────────────

/**
 * Given the elapsed time in ms and a total duration, derive which phase
 * the transition should be in for a given kind. Pure — the renderer calls
 * this per-frame to pick the right rendering branch.
 */
export function phaseAt(
  kind: TransitionKind,
  elapsedMs: number,
  durationMs: number,
): TransitionPhase {
  if (elapsedMs <= 0) return 'preparing';
  if (elapsedMs >= durationMs) return 'done';
  const bud = PHASE_BUDGETS[kind];
  const dur = Math.max(1, durationMs);
  const t = elapsedMs / dur;
  if (t < bud.preparing) return 'preparing';
  if (t < bud.preparing + bud.crossfading) return 'crossfading';
  if (t < 1) return 'settling';
  return 'done';
}

/**
 * Local progress within the current phase, in [0,1]. Useful for tweening
 * a single value (e.g. vignette alpha during the crossfading phase).
 */
export function phaseProgress(
  kind: TransitionKind,
  elapsedMs: number,
  durationMs: number,
): number {
  const bud = PHASE_BUDGETS[kind];
  const dur = Math.max(1, durationMs);
  const t = clamp01(elapsedMs / dur);
  if (t < bud.preparing) return clamp01(t / Math.max(1e-6, bud.preparing));
  if (t < bud.preparing + bud.crossfading) {
    return clamp01((t - bud.preparing) / Math.max(1e-6, bud.crossfading));
  }
  return clamp01((t - bud.preparing - bud.crossfading) / Math.max(1e-6, bud.settling));
}

/** Convenience — global progress in [0,1] across the whole transition. */
export function totalProgress(elapsedMs: number, durationMs: number): number {
  return clamp01(elapsedMs / Math.max(1, durationMs));
}

// ── audio crossfade math ───────────────────────────────────────────────────

export interface MusicCrossfadeGains {
  /** 0..1 gain for the outgoing music bed. */
  outgoing: number;
  /** 0..1 gain for the incoming music bed. */
  incoming: number;
}

/**
 * Equal-power music crossfade curve. Linear gain crossfades cause an
 * audible 3 dB dip at the midpoint (the "click/dip" the spec wants to
 * avoid). The cosine/sine pair below keeps the perceived loudness flat.
 */
export function musicCrossfadeGains(t: number): MusicCrossfadeGains {
  const x = clamp01(t);
  const half = Math.PI * 0.5;
  return {
    outgoing: Math.cos(x * half),
    incoming: Math.sin(x * half),
  };
}

/**
 * Minimal contract a sound needs to expose for the crossfader. Phaser's
 * `Phaser.Sound.WebAudioSound` and `HTML5AudioSound` both satisfy this via
 * their `volume` property and `stop()` method. Declared here (rather than
 * leaning on Phaser's types) so the crossfader stays Phaser-free and
 * unit-testable without a renderer.
 */
export interface CrossfadeSound {
  volume: number;
  stop(): void;
}

/**
 * Equal-power music crossfade for two sounds. Either side may be null —
 * the crossfader silently skips a missing one (the framework-test scene
 * runs with no audio assets and the spec's "no clicks/pops" rule still
 * holds; silence has no clicks).
 */
export class MusicCrossfader {
  /**
   * Apply equal-power gains for normalised time `t` ∈ [0,1]. Outgoing
   * fades out, incoming fades in, gains² sum to ~1 across the curve.
   */
  applyCrossfade(
    from: CrossfadeSound | null,
    to: CrossfadeSound | null,
    t: number,
    targetVolume = 1,
  ): void {
    if (from && from === to) {
      if (hasNumericVolume(from)) from.volume = targetVolume;
      return;
    }
    const gains = musicCrossfadeGains(t);
    if (from && hasNumericVolume(from)) from.volume = gains.outgoing * targetVolume;
    if (to && hasNumericVolume(to)) to.volume = gains.incoming * targetVolume;
  }

  /** Stop the outgoing sound after the curve has fully landed at t=1. */
  finishCrossfade(
    from: CrossfadeSound | null,
    to: CrossfadeSound | null,
    targetVolume = 1,
  ): void {
    if (from && from !== to) {
      try {
        from.stop();
      } catch {
        /* already stopped */
      }
    }
    if (to && hasNumericVolume(to)) to.volume = targetVolume;
  }
}

function hasNumericVolume(s: CrossfadeSound): boolean {
  return typeof (s as { volume?: unknown }).volume === 'number';
}

// ── camera-pan layout ──────────────────────────────────────────────────────

/**
 * For the camera-pan transition we render scenes A and B side-by-side in a
 * "tween painting" wider canvas. This helper computes the camera-x offset
 * (in canvas units) given a normalised pan progress in [0,1] and the
 * canvas width. At t=0 the camera frames A; at t=1 it frames B.
 */
export function cameraPanScrollX(
  panProgress: number,
  canvasWidth: number,
): number {
  return easeInOutCubic(panProgress) * canvasWidth;
}

// ── doorway zoom math ──────────────────────────────────────────────────────

export interface DoorwayCameraTarget {
  /** Camera centre in canvas pixels — this is what to scroll-to. */
  centerX: number;
  centerY: number;
  /** Camera zoom factor — >1 zooms in toward the door. */
  zoom: number;
}

/**
 * Compute the camera target for a doorway transition. `doorAnchor` is the
 * normalised centre of the door rect; `progress` is local progress within
 * the doorway-preparing phase ([0,1]). At progress=0 the camera sits at the
 * canvas centre with zoom=1; at progress=1 the camera is centred on the
 * door at zoom=`peakZoom`.
 */
export function doorwayCameraTarget(
  doorAnchor: NormalisedPoint,
  progress: number,
  canvasWidth: number,
  canvasHeight: number,
  peakZoom = 1.6,
): DoorwayCameraTarget {
  const t = easeInOutCubic(progress);
  const startX = canvasWidth / 2;
  const startY = canvasHeight / 2;
  const targetX = doorAnchor.x * canvasWidth;
  const targetY = doorAnchor.y * canvasHeight;
  return {
    centerX: startX + (targetX - startX) * t,
    centerY: startY + (targetY - startY) * t,
    zoom: 1 + (peakZoom - 1) * t,
  };
}

// ── plate alpha curves ─────────────────────────────────────────────────────

export interface PlateAlphaPair {
  /** 0..1 alpha for the outgoing scene's plate. */
  outgoing: number;
  /** 0..1 alpha for the incoming scene's plate. */
  incoming: number;
}

/**
 * Edge-crossfade plate alpha. Outgoing fades out; incoming fades in. Same
 * curve so plates are equal-weighted at the midpoint. Eased to keep the
 * fade from feeling like a digital alpha-lerp.
 */
export function edgeCrossfadeAlphas(progress: number): PlateAlphaPair {
  const t = easeInOutCubic(progress);
  return { outgoing: 1 - t, incoming: t };
}

/**
 * Doorway vignette alpha — black mask that opens, holds at full black for a
 * brief instant, then closes. Returns the vignette opacity in [0,1].
 *
 *   progress 0.00 → 0.45  fade up to opaque
 *   progress 0.45 → 0.55  hold
 *   progress 0.55 → 1.00  fade back out
 */
export function doorwayVignetteAlpha(progress: number): number {
  const t = clamp01(progress);
  if (t < 0.45) return smoothstep(t / 0.45);
  if (t < 0.55) return 1;
  return smoothstep(1 - (t - 0.55) / 0.45);
}

// ── threshold-zoom math ───────────────────────────────────────────────────

export interface ThresholdZoomCameraTarget {
  centerX: number;
  centerY: number;
  zoom: number;
}

/**
 * Camera target during the threshold-zoom prepare phase. Zooms deeper than
 * the basic doorway (peakZoom=2.5) so the door opening fills most of the
 * screen, providing a larger window for the incoming scene to show through.
 */
export function thresholdZoomCameraTarget(
  doorAnchor: NormalisedPoint,
  progress: number,
  canvasWidth: number,
  canvasHeight: number,
  peakZoom = 2.5,
): ThresholdZoomCameraTarget {
  const t = easeInOutCubic(progress);
  const startX = canvasWidth / 2;
  const startY = canvasHeight / 2;
  const targetX = doorAnchor.x * canvasWidth;
  const targetY = doorAnchor.y * canvasHeight;
  return {
    centerX: startX + (targetX - startX) * t,
    centerY: startY + (targetY - startY) * t,
    zoom: 1 + (peakZoom - 1) * t,
  };
}

/**
 * Door-frame mask rect in screen-space pixels, accounting for camera zoom.
 * At the start of the crossfade phase the mask matches the zoomed door rect;
 * as progress increases the mask expands to fill the whole screen.
 *
 * Returns `{ x, y, w, h }` in canvas pixels.
 */
export function thresholdZoomMaskRect(
  doorRect: { x: number; y: number; w: number; h: number },
  doorAnchor: NormalisedPoint,
  progress: number,
  canvasWidth: number,
  canvasHeight: number,
  peakZoom: number,
): { x: number; y: number; w: number; h: number } {
  const t = smoothstep(progress);

  const doorW = doorRect.w * canvasWidth * peakZoom;
  const doorH = doorRect.h * canvasHeight * peakZoom;
  const doorCx = canvasWidth / 2;
  const doorCy = canvasHeight / 2;

  const startX = doorCx - doorW / 2;
  const startY = doorCy - doorH / 2;
  const startW = doorW;
  const startH = doorH;

  const endX = 0;
  const endY = 0;
  const endW = canvasWidth;
  const endH = canvasHeight;

  return {
    x: startX + (endX - startX) * t,
    y: startY + (endY - startY) * t,
    w: startW + (endW - startW) * t,
    h: startH + (endH - startH) * t,
  };
}

/**
 * Alpha for the door-frame surround during threshold-zoom. The black frame
 * around the door mask fades in during prepare, holds during crossfade, and
 * fades out during settle.
 */
export function thresholdZoomFrameAlpha(globalProgress: number): number {
  const t = clamp01(globalProgress);
  if (t < 0.35) return smoothstep(t / 0.35);
  if (t < 0.75) return 1;
  return smoothstep(1 - (t - 0.75) / 0.25);
}

// ── walker continuity ──────────────────────────────────────────────────────

/**
 * Resolve the entry point on the target scene. Manifest's `EdgeZone.spawn`
 * always wins; an explicit `entryPoint` from the router caller is used only
 * when the zone has no spawn (defensive — schema should always set spawn).
 */
export function resolveEntryPoint(
  edgeSpawn: NormalisedPoint | undefined,
  fallback: NormalisedPoint | undefined,
  defaultSpawn: NormalisedPoint,
): NormalisedPoint {
  return edgeSpawn ?? fallback ?? defaultSpawn;
}

// ── edge-pan camera math ──────────────────────────────────────────────────

export interface EdgePanCameraTarget {
  scrollX: number;
  scrollY: number;
}

/**
 * Compute the camera scroll position for edge-pan rendering. Both zones are
 * placed in a shared world coordinate space via their `worldOrigin`; the
 * camera simply centres on the walker's world position, clamped so the
 * viewport doesn't overshoot the combined world bounds.
 *
 * All inputs and outputs are in world-space pixels.
 */
export function edgePanCameraTarget(
  walkerWorldX: number,
  walkerWorldY: number,
  viewportW: number,
  viewportH: number,
): EdgePanCameraTarget {
  return {
    scrollX: walkerWorldX - viewportW / 2,
    scrollY: walkerWorldY - viewportH / 2,
  };
}
